import React, {useState, useRef, useEffect} from 'react'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faDownload} from '@fortawesome/free-solid-svg-icons'
import LoadingSpinner, {LoadingSpinnerSize} from '../../common/LoadingSpinner'

interface MonthlyInstallmentInvoicesProps {
  folios: any[]
  checkinDate?: string
  checkoutDate?: string
  onDownloadSingle: (folio: any) => void
  downloadingInvoiceId?: string | null
}

const MonthlyInstallmentInvoices: React.FC<MonthlyInstallmentInvoicesProps> = ({
  folios,
  checkinDate,
  checkoutDate,
  onDownloadSingle,
  downloadingInvoiceId
}) => {
  const [showTopGradient, setShowTopGradient] = useState(false)
  const [showBottomGradient, setShowBottomGradient] = useState(false)
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)

  const handleScrollGradients = () => {
    const container = scrollContainerRef.current
    if (!container) {
      return
    }

    const {scrollTop, scrollHeight, clientHeight} = container
    const isAtTop = scrollTop === 0
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1

    setShowTopGradient(!isAtTop)
    setShowBottomGradient(!isAtBottom)
  }

  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) {
      return
    }

    handleScrollGradients()

    container.addEventListener('scroll', handleScrollGradients)
    return () => {
      container.removeEventListener('scroll', handleScrollGradients)
    }
  }, [folios])

  return (
    <div className="flex flex-col w-full pt-4">
      <h3 className="font-semibold text-lg mb-1">Monthly Rate Plan Invoices</h3>
      <p className="text-base text-gray-600 mb-6">
        Invoices will download as a pdf.
      </p>

      <div className="relative">
        <style>{`
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
        `}</style>
        {showTopGradient && folios.length > 3 && (
          <div
            className="absolute top-0 left-0 right-0 h-10
              bg-gradient-to-t from-white/0 to-white
              pointer-events-none z-[1]"
          />
        )}
        <div
          ref={scrollContainerRef}
          className="flex flex-col gap-2 hide-scrollbar pr-2 pb-2"
          style={{
            maxHeight: folios.length > 3 ? '200px' : 'auto',
            overflowY: folios.length > 3 ? 'auto' : 'visible',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}
        >
          {folios.map((folio: any, index: number) => {
            // Use billing period from API response
            const period =
              folio.billingPeriod?.formattedPeriod || 'Date pending'
            const installmentLabel =
              folio.billingPeriod?.installmentLabel ||
              `Installment ${index + 1}`

            return (
              <div
                key={folio.id}
                className={index < folios.length - 1 ? 'mb-3' : ''}
              >
                <div
                  className={`grid grid-cols-[280px_auto] gap-x-11 items-center min-h-[55px] ${
                    index < folios.length - 1 ? 'pb-3' : ''
                  }`}
                >
                  <div className="flex flex-col">
                    <div className="font-semibold text-sm">
                      {installmentLabel}
                    </div>
                    <div className="text-sm text-gray-600">{period}</div>
                  </div>
                  <button
                    onClick={() => onDownloadSingle(folio)}
                    className={`text-teal-700 transition-all text-sm
                      w-[22px] h-[22px] flex items-center
                      justify-center rounded-full ${
                        !folio.invoicePreviewPdf
                          ? 'opacity-30'
                          : 'opacity-100 hover:opacity-85 hover:bg-teal-700/10'
                      }`}
                    aria-label={`Download ${installmentLabel} invoice`}
                    disabled={
                      !folio.invoicePreviewPdf ||
                      downloadingInvoiceId === folio.id
                    }
                  >
                    {downloadingInvoiceId === folio.id ? (
                      <LoadingSpinner size={LoadingSpinnerSize.Small} />
                    ) : (
                      <FontAwesomeIcon icon={faDownload} className="text-sm" />
                    )}
                  </button>
                </div>
                {index < folios.length - 1 && (
                  <div className="w-[346px] h-px bg-gray-200" />
                )}
              </div>
            )
          })}
        </div>
        {showBottomGradient && folios.length > 3 && (
          <div
            className="absolute bottom-0 left-0 right-0 h-10
              bg-gradient-to-b from-white/0 to-white
              pointer-events-none z-[1]"
          />
        )}
      </div>
    </div>
  )
}

export default MonthlyInstallmentInvoices
